import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../minor-components/Card';
import { axiosInstance } from "../../constants/axiosInstance";
import { baseURL } from '../../constants/baseURL';

const TrendingProducts = ({ handleItem }) => {
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrendingProducts();
  }, []);

  const fetchTrendingProducts = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      // Get all orders
      const ordersResponse = await axiosInstance.get('/api/v1/order/getallordersfortrending', {
        headers: {
          "Authorization": token
        }
      });
      
      if (!ordersResponse.data.success) {
        throw new Error('Failed to fetch orders');
      }

      // Create sales map from orders
      const productSalesMap = new Map();
      ordersResponse.data.data.forEach(order => {
        order.details.forEach(detail => {
          if (detail.productId) { // Check if productId exists
            const productId = detail.productId._id;
            const currentCount = productSalesMap.get(productId) || 0;
            const quantity = parseInt(detail.quantity) || 0;
            productSalesMap.set(productId, currentCount + quantity);
          }
        });
      });

      // Get all products
      const productsResponse = await axiosInstance.get('/api/v1/product/getproducts');
      
      if (!productsResponse.data.success) {
        throw new Error('Failed to fetch products');
      }

      // Add sales data to products
      const productsWithSales = productsResponse.data.data
        .map(product => ({
          ...product,
          salesCount: productSalesMap.get(product._id) || 0
        }))
        .sort((a, b) => b.salesCount - a.salesCount)
        .slice(0, 4);

      // Set trending products with shop names
      setTrendingProducts(productsWithSales);
      setError(null);
    } catch (error) {
      console.error('Error fetching trending products:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4 text-center text-red-500">
        Error loading trending products: {error}
      </div>
    );
  }

  return (
    <motion.section
      className="bg-gradient-to-b from-purple-50 to-white py-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-[90vw] mx-auto">
        <motion.div 
          className="flex items-center justify-between mb-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <h2 className="text-3xl font-bold text-purple-900">Trending Now</h2>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-purple-600">Based on real sales data</span>
            <motion.div 
              className="w-2 h-2 rounded-full bg-green-500"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>

        <motion.div 
          className="grid lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {trendingProducts.map((product, index) => (
            <motion.div
              key={product._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className="relative"
            >
              {/* Trending Badge */}
              <div className="absolute top-4 right-4 z-10">
                <motion.div
                  className="bg-purple-900 text-white text-sm px-3 py-1 rounded-full"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  #{index + 1} Trending
                </motion.div>
              </div>
              
              <Card
                svg={`${baseURL}${product.productPhoto}`}
                title={product.name}
                desc={product.description}
                price={product.price}
                item={product}
                shopName={product.shopName}
                getItem={() => handleItem(product)}
              />
              
              {/* Sales Indicator */}
              <motion.div
                className="mt-2 text-sm text-gray-600 flex items-center justify-end"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
              >
                <svg 
                  className="w-4 h-4 mr-1 text-purple-600" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                {product.salesCount} sold
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default TrendingProducts;